export interface TaskImportRow {
  line: number;
  title: string;
  section: string;
  durationDays: number;
  startDate: string;
}

export interface TaskImportParseResult {
  rows: TaskImportRow[];
  errors: string[];
}

export const TASK_IMPORT_TEMPLATE =
  "Task\tSection\tDuration\tStart date\nConfirm project kickoff\tProject management\t1\t2026-08-03";

function parseTsv(source: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]!;
    if (character === '"') {
      if (quoted && source[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else quoted = !quoted;
      continue;
    }
    if (!quoted && character === "\t") {
      row.push(cell);
      cell = "";
      continue;
    }
    if (!quoted && (character === "\n" || character === "\r")) {
      if (character === "\r" && source[index + 1] === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += character;
  }
  row.push(cell);
  rows.push(row);
  return rows;
}

function validDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month! - 1 &&
    date.getUTCDate() === day
  );
}

function normalizeDate(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (validDate(trimmed)) return trimmed;
  const numeric = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (!numeric) return "";
  const normalized = `${numeric[3]}-${numeric[1]!.padStart(2, "0")}-${numeric[2]!.padStart(2, "0")}`;
  return validDate(normalized) ? normalized : "";
}

export function parseTaskImport(
  source: string,
  projectStartDate: string,
): TaskImportParseResult {
  const errors: string[] = [];
  const rows: TaskImportRow[] = [];
  const parsed = parseTsv(source).filter((cells) =>
    cells.some((cell) => cell.trim()),
  );
  const first = parsed[0]?.map((cell) => cell.trim().toLocaleLowerCase());
  const hasHeader =
    first?.[0] === "task" &&
    (first.length === 1 ||
      first[1] === "section" ||
      first[2] === "duration" ||
      first[3] === "start date");
  const body = hasHeader ? parsed.slice(1) : parsed;
  body.forEach((cells, index) => {
    const line = index + (hasHeader ? 2 : 1);
    const [titleCell = "", sectionCell = "", durationCell = "", dateCell = ""] =
      cells;
    const title = titleCell.trim();
    if (!title) {
      errors.push(`Row ${line}: add a task name.`);
      return;
    }
    if (cells.slice(4).some((cell) => cell.trim())) {
      errors.push(
        `Row ${line}: use only Task, Section, Duration, and Start date.`,
      );
      return;
    }
    const duration = durationCell.trim() ? Number(durationCell.trim()) : 1;
    if (!Number.isInteger(duration) || duration < 1 || duration > 1_000) {
      errors.push(
        `Row ${line}: duration must be a whole number from 1 to 1,000.`,
      );
      return;
    }
    const normalizedDate = normalizeDate(dateCell);
    if (normalizedDate === "") {
      errors.push(`Row ${line}: use YYYY-MM-DD or M/D/YYYY for Start date.`);
      return;
    }
    rows.push({
      line,
      title,
      section: sectionCell.trim(),
      durationDays: duration,
      startDate: normalizedDate || projectStartDate,
    });
  });
  if (!rows.length && !errors.length) errors.push("Paste at least one task.");
  if (rows.length > 80) errors.push("Import up to 80 tasks at a time.");
  return { rows: rows.slice(0, 80), errors };
}

export function parseItemLines(source: string): string[] {
  return source
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}
